import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { fetchAndActivate, getBoolean, getRemoteConfig } from 'firebase/remote-config'
import type { EngineRegistry, ExperienceEvent, ExperiencePerformanceTier, ExperienceReveal, PendingCelebration } from '../types/experienceEngine'
import { firebaseApp, firebaseFunctions, firestoreDb } from './firebase'
import { loadEngineRegistry } from './engineRegistry'
import { subscribeExperienceEvents } from '../utils/experienceEvents'

export type ExperienceProcessResult = {
  duplicate: boolean
  eventId: string
  celebrationQueued: boolean
  feedbackFormId: string | null
}

type EngineCallbacks = {
  onRegistry: (registry: EngineRegistry) => void
  onProcessed: (results: ExperienceProcessResult[]) => void
  onReveal: (reveal: ExperienceReveal) => void
  onEnabled: (enabled: boolean) => void
  onError: (error: Error) => void
}

let visualsEnabled = false
export const areExperienceVisualsEnabled = () => visualsEnabled

const MAX_EVENT_RETRY_ATTEMPTS = 3
const BASE_EVENT_RETRY_DELAY_MS = 3_000
const MAX_EVENT_RETRY_DELAY_MS = 60_000
const MAINTENANCE_RETRY_DELAY_MS = 30 * 60_000
const EVENT_FAILURE_WINDOW_MS = 60_000
const RETRYABLE_EVENT_ERROR_CODES = new Set([
  'functions/deadline-exceeded',
  'functions/internal',
  'functions/resource-exhausted',
  'functions/unavailable',
  'deadline-exceeded',
  'internal',
  'resource-exhausted',
  'unavailable',
])

const firebaseErrorCode = (error: unknown) => {
  const code = (error as { code?: unknown })?.code
  return typeof code === 'string' ? code : ''
}

const firebaseErrorMessage = (error: unknown) => {
  const message = (error as { message?: unknown })?.message
  return typeof message === 'string' ? message : ''
}

const isMaintenanceError = (error: unknown) => {
  const message = firebaseErrorMessage(error).toLowerCase()
  return message.includes('maintenance') || message.includes('service unavailable')
}

const presenterId = () => {
  const key = 'timelining.experience-presenter-id'
  const existing = window.sessionStorage.getItem(key)
  if (existing) return existing
  const id = crypto.randomUUID().replaceAll('-', '_')
  window.sessionStorage.setItem(key, id)
  return id
}

const performanceTier = (registry: EngineRegistry): ExperiencePerformanceTier => {
  const policy = registry.performance.selection
  const device = navigator as Navigator & { connection?: { saveData?: boolean }; deviceMemory?: number }
  if (policy.respectReducedMotion && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 'REDUCED_MOTION'
  if (policy.lowTierWhenSaveDataEnabled && device.connection?.saveData) return 'LOW'
  if (policy.lowTierOnMobile && window.matchMedia('(max-width: 767px)').matches) return 'LOW'
  if (navigator.hardwareConcurrency <= policy.lowTierWhenHardwareConcurrencyAtMost) return 'LOW'
  if (device.deviceMemory !== undefined && device.deviceMemory <= policy.lowTierWhenDeviceMemoryGbAtMost) return 'LOW'
  return 'HIGH'
}

const featureEnabled = async (registry: EngineRegistry) => {
  if (import.meta.env.VITE_ENGINE_V2_ACTIVE === 'true') return true
  if (import.meta.env.VITE_ENGINE_V2_ACTIVE === 'false') return false
  try {
    const remoteConfig = getRemoteConfig(firebaseApp)
    remoteConfig.settings = { fetchTimeoutMillis: 5_000, minimumFetchIntervalMillis: 60_000 }
    remoteConfig.defaultConfig = { [registry.engine.featureFlag]: false }
    await fetchAndActivate(remoteConfig)
    return getBoolean(remoteConfig, registry.engine.featureFlag)
  } catch (error) {
    console.warn('[ExperienceEngine] Remote Config unavailable; visuals remain disabled.', error)
    return false
  }
}

const processEvent = httpsCallable<Record<string, unknown>, ExperienceProcessResult>(firebaseFunctions, 'processExperienceEvent')

type BatchResult = {
  results: Array<
    | { ok: true; data: ExperienceProcessResult }
    | { ok: false; error: { code: string; message: string } }
  >
}

const processEventsBatch = httpsCallable<Record<string, unknown>, BatchResult>(firebaseFunctions, 'processExperienceEventsBatch')

/**
 * Debounce window for flushing queued experience events. Multiple events
 * dispatched inside this window (e.g. login pulse + page-open + workspace
 * pulse in a single navigation) coalesce into ONE batched Cloud Function
 * call (see `processExperienceEventsBatch`). 500ms strikes the balance
 * between "batches feel snappy" and "no unnecessary round-trips".
 */
const FLUSH_DEBOUNCE_MS = 500

export class ExperienceEngine {
  private readonly presenter = presenterId()
  private readonly uid: string
  private readonly callbacks: EngineCallbacks
  private registry: EngineRegistry | null = null
  private queue: ExperienceEvent[] = []
  private pending: PendingCelebration[] = []
  private pendingRevision = ''
  private unsubscribeEvents: (() => void) | null = null
  private unsubscribePending: Unsubscribe | null = null
  private collectionTimer: number | null = null
  private reactivationTimer: number | null = null
  private retryTimer: number | null = null
  private flushTimer: number | null = null
  private retryAttempts = new Map<string, number>()
  private recentFlushFailures: number[] = []
  private maintenanceUntil = 0
  private maintenanceReported = false
  private processing = false
  private revealing = false

  constructor(uid: string, callbacks: EngineCallbacks) {
    this.uid = uid
    this.callbacks = callbacks
  }

  async start(): Promise<void> {
    this.unsubscribeEvents = subscribeExperienceEvents((event) => {
      this.queue.push(event)
      this.scheduleFlush()
    })
    try {
      this.registry = await loadEngineRegistry()
      this.callbacks.onRegistry(this.registry)
      visualsEnabled = await featureEnabled(this.registry)
      this.callbacks.onEnabled(visualsEnabled)
      if (visualsEnabled) {
        this.unsubscribePending = onSnapshot(
          doc(firestoreDb, 'users', this.uid, 'gamification', 'pending_celebrations'),
          (snapshot) => {
            this.pending = Array.isArray(snapshot.data()?.items) ? snapshot.data()?.items as PendingCelebration[] : []
            const revision = this.pending.map((item) => item.id).join('|')
            if (revision !== this.pendingRevision) {
              this.pendingRevision = revision
              this.scheduleCollection()
            }
          },
          (error) => this.callbacks.onError(error),
        )
      }
      document.addEventListener('visibilitychange', this.handleVisibility)
      this.recordLogin()
      await this.flushEvents()
    } catch (error) {
      this.callbacks.onError(error instanceof Error ? error : new Error('Experience Engine failed to initialize.'))
    }
  }

  stop(): void {
    this.unsubscribeEvents?.()
    this.unsubscribePending?.()
    document.removeEventListener('visibilitychange', this.handleVisibility)
    this.clearTimers()
    if (this.flushTimer !== null) {
      window.clearTimeout(this.flushTimer)
      this.flushTimer = null
    }
    this.queue = []
    this.pending = []
    this.retryAttempts.clear()
    this.recentFlushFailures = []
    this.maintenanceUntil = 0
    this.maintenanceReported = false
    visualsEnabled = false
  }

  async acknowledge(items: PendingCelebration[]): Promise<void> {
    if (!this.registry) return
    try {
      await acknowledgePendingExperienceItems(this.registry.registryVersion, this.presenter, items.map((item) => item.id))
    } catch (error) {
      this.callbacks.onError(error instanceof Error ? error : new Error('Celebration acknowledgement failed.'))
    } finally {
      this.revealing = false
      this.pending = []
      this.pendingRevision = ''
      this.clearTimers()
    }
  }

  /**
   * Debounce queued events so bursts of dispatches (login pulse + page-open +
   * workspace pulse arriving in the same tick) settle into one batched flush.
   * Also acts as a natural batching window for the new
   * `processExperienceEventsBatch` callable.
   */
  private scheduleFlush(): void {
    if (this.flushTimer !== null) return
    this.flushTimer = window.setTimeout(() => {
      this.flushTimer = null
      void this.flushEvents()
    }, FLUSH_DEBOUNCE_MS)
  }

  private async flushEvents(): Promise<void> {
    if (!this.registry || this.processing || this.queue.length === 0) return
    if (this.isMaintenanceCooldownActive()) {
      this.scheduleMaintenanceRetry()
      return
    }
    this.processing = true
    const events = this.queue.splice(0)
    const payloads = events.map((event) => ({
      eventId: event.eventId.replaceAll('-', '_'),
      action: event.actionType,
      workspaceId: event.payload.workspaceId ?? null,
      sourceId: event.payload.sourceId ?? event.payload.subNodeId ?? null,
      occurredAt: event.occurredAt,
      payload: event.payload,
      clientVisible: document.visibilityState === 'visible',
      celebrationsEnabled: visualsEnabled,
    }))
    // Batch path: >1 event in the flush window → one callable round-trip.
    // Single path: kept for single-event flushes so we don't grow the payload
    // envelope needlessly and so tests / older backend deployments keep working.
    const registryVersion = this.registry?.registryVersion
    let wholesaleBatchError: unknown = null
    const settled: PromiseSettledResult<{ data: ExperienceProcessResult }>[] = payloads.length > 1
      ? await (async () => {
          try {
            const response = await processEventsBatch({ registryVersion, events: payloads })
            return response.data.results.map((result) => (
              result.ok
                ? ({ status: 'fulfilled', value: { data: result.data } } as PromiseSettledResult<{ data: ExperienceProcessResult }>)
                : ({ status: 'rejected', reason: Object.assign(new Error(result.error.message), { code: result.error.code }) } as PromiseSettledResult<{ data: ExperienceProcessResult }>)
            ))
          } catch (error) {
            // Whole batch failed — reuse the same error identity so the
            // per-event retry logic below runs while onError only fires once.
            wholesaleBatchError = error
            return payloads.map(() => ({ status: 'rejected', reason: error } as PromiseSettledResult<{ data: ExperienceProcessResult }>))
          }
        })()
      : await Promise.allSettled(payloads.map((event) => processEvent({ registryVersion, event })))
    const completed: ExperienceProcessResult[] = []
    let maxRetryAttempt = 0
    let sawRetryableFailure = false
    let wholesaleErrorReported = false
    settled.forEach((result, index) => {
      const event = events[index]
      if (result.status === 'fulfilled') {
        this.retryAttempts.delete(event.eventId)
        this.recentFlushFailures = []
        completed.push(result.value.data)
      }
      else {
        const errorCode = firebaseErrorCode(result.reason)
        if (isMaintenanceError(result.reason)) {
          this.queue.push(event)
          this.enterMaintenanceCooldown(result.reason)
          return
        }
        const attempts = (this.retryAttempts.get(event.eventId) ?? 0) + 1
        if (RETRYABLE_EVENT_ERROR_CODES.has(errorCode) && attempts <= MAX_EVENT_RETRY_ATTEMPTS) {
          this.retryAttempts.set(event.eventId, attempts)
          this.queue.push(event)
          maxRetryAttempt = Math.max(maxRetryAttempt, attempts)
          sawRetryableFailure = true
        } else {
          this.retryAttempts.delete(event.eventId)
        }
        const isWholesale = wholesaleBatchError !== null && result.reason === wholesaleBatchError
        if (isWholesale && wholesaleErrorReported) return
        if (isWholesale) wholesaleErrorReported = true
        this.callbacks.onError(result.reason instanceof Error ? result.reason : new Error('Experience event failed.'))
      }
    })
    if (sawRetryableFailure && this.shouldEnterFlushCooldown()) {
      this.enterMaintenanceCooldown(new Error('Experience Engine is temporarily cooling down after repeated callable failures.'))
    }
    this.processing = false
    if (completed.length > 0) this.callbacks.onProcessed(completed)
    if (this.queue.length > 0) {
      if (this.isMaintenanceCooldownActive()) {
        this.scheduleMaintenanceRetry()
      } else {
        const delay = Math.min(MAX_EVENT_RETRY_DELAY_MS, BASE_EVENT_RETRY_DELAY_MS * 2 ** Math.max(0, maxRetryAttempt - 1))
        this.retryTimer = window.setTimeout(() => void this.flushEvents(), delay)
      }
    }
  }

  private isMaintenanceCooldownActive() {
    return this.maintenanceUntil > Date.now()
  }

  private enterMaintenanceCooldown(reason: unknown) {
    this.maintenanceUntil = Date.now() + MAINTENANCE_RETRY_DELAY_MS
    if (!this.maintenanceReported) {
      this.maintenanceReported = true
      this.callbacks.onError(reason instanceof Error ? reason : new Error('Experience Engine is temporarily unavailable.'))
    }
  }

  private shouldEnterFlushCooldown() {
    const now = Date.now()
    this.recentFlushFailures = this.recentFlushFailures.filter((timestamp) => now - timestamp <= EVENT_FAILURE_WINDOW_MS)
    this.recentFlushFailures.push(now)
    return this.recentFlushFailures.length >= MAX_EVENT_RETRY_ATTEMPTS
  }

  private scheduleMaintenanceRetry() {
    if (this.retryTimer !== null) return
    const delay = Math.max(1_000, this.maintenanceUntil - Date.now())
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null
      this.maintenanceReported = false
      void this.flushEvents()
    }, delay)
  }

  private recordLogin() {
    const day = new Date().toISOString().slice(0, 10).replaceAll('-', '_')
    const occurredAt = new Date().toISOString()
    this.queue.push(
      { eventId: `login_total_${day}`, actionType: 'TRACK_TOTAL_LOGINS', payload: {}, occurredAt },
      { eventId: `login_streak_${day}`, actionType: 'TRACK_STREAK_LOGINS', payload: {}, occurredAt },
    )
  }

  private readonly handleVisibility = () => {
    this.clearTimers()
    if (document.visibilityState !== 'visible' || !this.registry || this.pending.length === 0) return
    this.reactivationTimer = window.setTimeout(() => void this.flushCelebrations(), this.registry.engine.timing.reactivationDelayMs)
  }

  private scheduleCollection() {
    if (!this.registry || this.pending.length === 0 || this.revealing || document.visibilityState !== 'visible') return
    if (this.collectionTimer !== null) window.clearTimeout(this.collectionTimer)
    this.collectionTimer = window.setTimeout(() => void this.flushCelebrations(), this.registry.engine.timing.collectionWindowMs)
  }

  private async flushCelebrations() {
    if (!this.registry || this.revealing || this.pending.length === 0 || document.visibilityState !== 'visible') return
    this.clearTimers()
    try {
      const items = await claimPendingExperienceItems(this.registry.registryVersion, this.presenter)
      if (items.length === 0) return
      this.revealing = true
      this.callbacks.onReveal({ presenterId: this.presenter, items, registry: this.registry, performanceTier: performanceTier(this.registry) })
    } catch (error) {
      this.callbacks.onError(error instanceof Error ? error : new Error('Celebration claim failed.'))
    }
  }

  private clearTimers() {
    if (this.collectionTimer !== null) window.clearTimeout(this.collectionTimer)
    if (this.reactivationTimer !== null) window.clearTimeout(this.reactivationTimer)
    if (this.retryTimer !== null) window.clearTimeout(this.retryTimer)
    this.collectionTimer = null
    this.reactivationTimer = null
    this.retryTimer = null
  }
}

export const claimPendingExperienceItems = async (registryVersion: string, presenterId: string): Promise<PendingCelebration[]> => {
  const call = httpsCallable<Record<string, unknown>, { items: PendingCelebration[] }>(firebaseFunctions, 'claimPendingExperienceItems')
  return (await call({ registryVersion, presenterId })).data.items
}

export const acknowledgePendingExperienceItems = async (registryVersion: string, presenterId: string, itemIds: string[]) => {
  const call = httpsCallable<Record<string, unknown>, { acknowledged: number }>(firebaseFunctions, 'acknowledgePendingExperienceItems')
  return (await call({ registryVersion, presenterId, itemIds })).data
}

export const saveFeedbackDraft = async (registryVersion: string, formId: string, rating: number | null, remark: string) => {
  const call = httpsCallable(firebaseFunctions, 'saveExperienceFeedbackDraft')
  await call({ registryVersion, formId, rating, remark })
}

export type FeedbackQueueItem = {
  formId: string
  rating: number | null
  remark: string
  queueState: 'READY' | 'SKIPPED'
  skippedAt: string | null
  sourceAction?: string | null
}

export const getFeedbackQueue = async (registryVersion: string) => {
  const call = httpsCallable<Record<string, unknown>, { forms: FeedbackQueueItem[] }>(firebaseFunctions, 'getExperienceFeedbackQueue')
  return (await call({ registryVersion })).data.forms
}

export const submitFeedback = async (registryVersion: string, formId: string, rating: number | null, remark: string, skipped: boolean) => {
  const call = httpsCallable<Record<string, unknown>, { duplicate: boolean; creditsAwarded: number }>(firebaseFunctions, 'submitExperienceFeedback')
  return (await call({ registryVersion, formId, rating, remark, skipped, submissionId: crypto.randomUUID().replaceAll('-', '_') })).data
}

export const discardFeedback = async (registryVersion: string, formId: string) => {
  const call = httpsCallable<Record<string, unknown>, { discarded: boolean }>(firebaseFunctions, 'discardExperienceFeedback')
  return (await call({ registryVersion, formId })).data
}
